import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useFranchises, useGuestFranchises } from '@/hooks/useFranchise';
import { useAuthStore } from '@/store/authStore';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/lib/utils';
import type { FranchiseFilter as FranchiseFilterType, Franchise } from '@/types';
import FranchiseCard from '@/components/card/FranchiseCard';
import { FranchiseFilter } from '@/components/franchise/FranchiseFilter';
import { Select, MenuItem } from '@mui/material';

const LIMIT = 9;

export default function FranchiseListPage() {
  const { isAuthenticated, user } = useAuthStore();
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<FranchiseFilterType>({
    page: 1,
    limit: LIMIT,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });

  const { data, isLoading, isFetching } = !isAuthenticated ? useGuestFranchises(filters) : useFranchises(filters);
  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  /* Sync ?q= từ Navbar */
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setFilters((f) => ({ ...f, search: q, page: 1 }));
    }
  }, [searchParams.get('q')]);

  const handleFilterSearch = (newFilters: any) => {
    setFilters((f) => {
      // Retain limit and order options, overwrite other filters
      return {
        limit: f.limit,
        orderBy: f.orderBy,
        orderDirection: f.orderDirection,
        ...newFilters,
        page: 1,
      };
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Nhượng quyền</div>
        <div className="flex items-end justify-between flex-wrap gap-2">
          <h1 className="section-title mb-0">Danh sách nhượng quyền</h1>
          <div className="flex items-center gap-3">
            {total > 0 && (
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>
                {total} kết quả
              </span>
            )}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors hover:border-orange-400 hover:text-orange-500"
                style={{ borderColor: 'var(--border-c)', color: 'var(--text-3)' }}
              >
                <Lock size={11} /> Đăng nhập để lọc nâng cao
              </Link>
            )}
          </div>
        </div>
      </div>


      <div className="sticky top-[104px] z-30 mb-8">
        <FranchiseFilter onSearch={handleFilterSearch} initialFilters={filters} />
      </div>

      {/* Sort options aligned right */}
      <div className="flex justify-end mb-4">
        <Select
          value={`${filters.orderBy}|${filters.orderDirection}`}
          onChange={(e) => {
            const [orderBy, orderDirection] = (e.target.value as string).split('|') as [any, 'ASC' | 'DESC'];
            setFilters(prev => ({ ...prev, orderBy, orderDirection, page: 1 }));
          }}
          size="small"
          sx={{
            minWidth: '160px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            }
          }}
        >
          <MenuItem value="createdAt|DESC" sx={{ fontSize: '14px' }}>Mới nhất</MenuItem>
          <MenuItem value="investmentMin|ASC" sx={{ fontSize: '14px' }}>Đầu tư tăng dần</MenuItem>
          <MenuItem value="investmentMin|DESC" sx={{ fontSize: '14px' }}>Đầu tư giảm dần</MenuItem>
          <MenuItem value="franchiseFee|ASC" sx={{ fontSize: '14px' }}>Phí NQ tăng dần</MenuItem>
          <MenuItem value="totalOutlets|DESC" sx={{ fontSize: '14px' }}>Nhiều chi nhánh nhất</MenuItem>
        </Select>
      </div>

      {/* Results */}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState title="Không tìm thấy nhượng quyền" description="Thử thay đổi bộ lọc hoặc tìm kiếm khác" icon="🏪" />
      ) : (
        <>
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetching && 'opacity-60')}>
            {items.map((item, i) => (
              <div key={item.id} style={{ animationDelay: `${i * 40}ms` }} className="animate-fade-up">
                <FranchiseCard item={item} />
              </div>
            ))}
          </div>

          <Pagination page={filters.page ?? 1} totalPages={totalPages} onChange={(p) => setFilters((f) => ({ ...f, page: p }))} />
        </>
      )}
    </div>
  );
}
